import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  Timeline,
  Assessment,
  Download,
  Refresh,
  PlayArrow,
  Pause,
  ErrorOutline,
  CheckCircle,
  Visibility
} from '@mui/icons-material';
import { FileProgressTracker } from '../components/FileProgressTracker';
import { DataSourceFileUploader } from '../components/DataSourceFileUploader';
import auditService, { 
  AuditEventType, 
  AuditSeverity, 
  ProcessingStage, 
  AuditSummary,
  AuditEvent 
} from '../services/audit/auditService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`demo-tabpanel-${index}`}
      aria-labelledby={`demo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const FileProgressAuditDemo: React.FC = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [recentEvents, setRecentEvents] = useState<AuditEvent[]>([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationDialog, setSimulationDialog] = useState(false);

  // Demo client and workflow IDs
  const demoClientId = 'demo-client-001';
  const demoWorkflowId = 'demo-workflow-001';

  // Load audit data
  const loadAuditData = useCallback(async () => {
    try {
      const summary = await auditService.getAuditSummary(demoClientId, demoWorkflowId);
      setAuditSummary(summary);
      
      const events = auditService.getRecentEvents(20);
      setRecentEvents(events);
    } catch (error) {
      console.error('Error loading audit data:', error);
    }
  }, [demoClientId, demoWorkflowId]);

  // Setup event listeners
  useEffect(() => {
    const handleAuditEvent = (event: AuditEvent) => {
      loadAuditData();
    };

    // Listen to all event types for demo
    Object.values(AuditEventType).forEach(eventType => {
      auditService.addEventListener(eventType, handleAuditEvent);
    });

    // Initial load
    loadAuditData();

    // Auto refresh
    const interval = setInterval(loadAuditData, 5000);

    return () => {
      Object.values(AuditEventType).forEach(eventType => {
        auditService.removeEventListener(eventType, handleAuditEvent);
      });
      clearInterval(interval);
    };
  }, [loadAuditData]);

  // Handle file uploads
  const handleFilesAdded = useCallback(async (dataSource: string, files: File[]) => {
    for (const file of files) {
      const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Log upload start
      await auditService.logFileUploadStart(
        demoClientId,
        demoWorkflowId,
        fileId,
        file.name,
        dataSource,
        file.size
      );

      // Simulate upload progress
      simulateFileUpload(fileId, file.name, dataSource);
    }
  }, [demoClientId, demoWorkflowId]);

  // Handle file removal
  const handleFileRemoved = useCallback(async (dataSource: string, fileId: string) => {
    await auditService.logEvent(
      AuditEventType.FILE_DELETE,
      AuditSeverity.INFO,
      `File removed from ${dataSource}`,
      {
        clientId: demoClientId,
        workflowId: demoWorkflowId,
        fileId,
        dataSource
      }
    );
  }, [demoClientId, demoWorkflowId]);

  // Simulate file upload process
  const simulateFileUpload = async (fileId: string, fileName: string, dataSource: string) => {
    const totalSteps = 100;
    const stepDelay = 50; // milliseconds per step
    let currentProgress = 0;

    const updateProgress = async (progress: number, stage: ProcessingStage) => {
      setUploadProgress(prev => new Map(prev.set(fileId, progress)));
      
      await auditService.logFileUploadProgress(
        demoClientId,
        demoWorkflowId,
        fileId,
        fileName,
        progress
      );

      if (progress === 100) {
        await auditService.logFileUploadComplete(
          demoClientId,
          demoWorkflowId,
          fileId,
          fileName,
          `${dataSource}/${fileName}`,
          Date.now() - performance.now()
        );

        // Simulate processing stages
        setTimeout(() => simulateFileProcessing(fileId, fileName, dataSource), 1000);
      }
    };

    // Simulate upload progress
    const uploadInterval = setInterval(async () => {
      currentProgress += Math.random() * 10;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(uploadInterval);
      }
      await updateProgress(currentProgress, ProcessingStage.UPLOADING);
    }, stepDelay);
  };

  // Simulate file processing
  const simulateFileProcessing = async (fileId: string, fileName: string, dataSource: string) => {
    const stages = [
      ProcessingStage.VALIDATING,
      ProcessingStage.PARSING,
      ProcessingStage.PROCESSING,
      ProcessingStage.ANALYZING,
      ProcessingStage.COMPLETED
    ];

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      const progress = ((i + 1) / stages.length) * 100;

      await auditService.logProcessingStageChange(
        demoClientId,
        demoWorkflowId,
        fileId,
        fileName,
        stage,
        progress,
        `Processing ${fileName} - ${stage}`
      );

      // Random delay between stages
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    }
  };

  // Simulate bulk operations
  const simulateBulkOperations = async () => {
    setIsSimulating(true);
    
    const operations = [
      { fileName: 'semrush_keywords.csv', dataSource: 'semrush', size: 1024 * 50 },
      { fileName: 'gsc_performance.csv', dataSource: 'gsc', size: 1024 * 75 },
      { fileName: 'google_ads_data.csv', dataSource: 'google_ads', size: 1024 * 30 },
      { fileName: 'lpm_rankings.csv', dataSource: 'lpm', size: 1024 * 40 },
      { fileName: 'ga4_analytics.csv', dataSource: 'ga4', size: 1024 * 60 }
    ];

    for (const op of operations) {
      const fileId = `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      await auditService.logFileUploadStart(
        demoClientId,
        demoWorkflowId,
        fileId,
        op.fileName,
        op.dataSource,
        op.size
      );

      // Start upload simulation
      simulateFileUpload(fileId, op.fileName, op.dataSource);
      
      // Small delay between files
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsSimulating(false);
  };

  // Simulate errors
  const simulateErrors = async () => {
    const fileId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileName = 'corrupted_file.csv';
    const dataSource = 'custom';

    await auditService.logFileUploadStart(
      demoClientId,
      demoWorkflowId,
      fileId,
      fileName,
      dataSource
    );

    // Simulate upload progress then error
    setTimeout(async () => {
      await auditService.logFileUploadError(
        demoClientId,
        demoWorkflowId,
        fileId,
        fileName,
        new Error('Network timeout during upload'),
        0
      );
    }, 2000);
  };

  // Export audit data
  const exportAuditData = async () => {
    try {
      const blob = await auditService.exportAuditData('json', {
        clientId: demoClientId,
        workflowId: demoWorkflowId
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-data-${demoClientId}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting audit data:', error);
    }
  };

  const formatEventType = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getSeverityColor = (severity: AuditSeverity) => {
    switch (severity) {
      case AuditSeverity.ERROR:
      case AuditSeverity.CRITICAL:
        return 'error';
      case AuditSeverity.WARNING:
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          File Progress Tracking & Auditing Demo
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive file operation monitoring with real-time progress tracking and detailed audit trails
        </Typography>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={simulateBulkOperations}
            disabled={isSimulating}
          >
            Simulate Uploads
          </Button>
          <Button
            variant="outlined"
            startIcon={<ErrorOutline />}
            onClick={simulateErrors}
            color="error"
          >
            Simulate Errors
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportAuditData}
          >
            Export Audit Data
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => setSimulationDialog(true)}
          >
            View Details
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {auditSummary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Timeline sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Total Events</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {auditSummary.totalEvents}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CloudUpload sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">Active Operations</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {auditSummary.activeOperations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ErrorOutline sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="h6">Errors</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {auditSummary.errorCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Success Rate</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {Math.round(auditSummary.successRate * 100)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="File Upload Interface" />
            <Tab label="Progress Tracker" />
            <Tab label="Audit Events" />
            <Tab label="System Analytics" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <DataSourceFileUploader
            onFilesAdded={handleFilesAdded}
            onFileRemoved={handleFileRemoved}
            uploadProgress={uploadProgress}
            clientId={demoClientId}
            workflowId={demoWorkflowId}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <FileProgressTracker
            clientId={demoClientId}
            workflowId={demoWorkflowId}
            showCompleted={true}
            maxItems={50}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Recent Audit Events
            </Typography>
            {recentEvents.length === 0 ? (
              <Alert severity="info">
                No recent events. Try uploading some files or running the simulation.
              </Alert>
            ) : (
              <List>
                {recentEvents.map((event) => (
                  <ListItem key={event.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{event.message}</Typography>
                          <Chip
                            label={getSeverityColor(event.severity)}
                            color={getSeverityColor(event.severity)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatEventType(event.eventType)} • {new Date(event.timestamp).toLocaleString()}
                          </Typography>
                          {event.fileName && (
                            <Typography variant="caption" display="block">
                              File: {event.fileName} • Source: {event.dataSource}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Operations by Stage
                  </Typography>
                  {auditSummary?.operationsByStage && Object.entries(auditSummary.operationsByStage).map(([stage, count]) => (
                    <Box key={stage} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{stage.replace(/_/g, ' ')}</Typography>
                        <Typography variant="body2">{count}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={auditSummary.activeOperations > 0 ? (count / auditSummary.activeOperations) * 100 : 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Common Errors
                  </Typography>
                  {auditSummary?.mostCommonErrors && auditSummary.mostCommonErrors.length > 0 ? (
                    auditSummary.mostCommonErrors.map((error, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        <Typography variant="body2" noWrap>
                          {error.error}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {error.count} occurrences
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No errors recorded
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Simulation Details Dialog */}
      <Dialog
        open={simulationDialog}
        onClose={() => setSimulationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Demo System Details</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            This demo showcases a comprehensive file progress tracking and auditing system with the following features:
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Audit Service Features:
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Real-time event logging for all file operations" /></ListItem>
            <ListItem><ListItemText primary="Offline caching with automatic sync when online" /></ListItem>
            <ListItem><ListItemText primary="Progress tracking across multiple processing stages" /></ListItem>
            <ListItem><ListItemText primary="Error tracking with retry mechanisms" /></ListItem>
            <ListItem><ListItemText primary="Event listener system for real-time updates" /></ListItem>
            <ListItem><ListItemText primary="Export capabilities for audit data" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Progress Tracker Features:
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Real-time file operation status display" /></ListItem>
            <ListItem><ListItemText primary="Visual progress indicators with estimated completion" /></ListItem>
            <ListItem><ListItemText primary="Error states with retry options" /></ListItem>
            <ListItem><ListItemText primary="Compact and detailed view modes" /></ListItem>
            <ListItem><ListItemText primary="Auto-refresh with manual refresh option" /></ListItem>
            <ListItem><ListItemText primary="Recent activity timeline for each file" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Demo Data:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Client ID: {demoClientId}<br />
            Workflow ID: {demoWorkflowId}<br />
            All events are simulated and stored locally for demonstration purposes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulationDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FileProgressAuditDemo;
